import React, { forwardRef, ReactNode, RefObject, useEffect, useImperativeHandle, useRef, useState } from 'react'
import styled from 'styled-components'
import { useCreationDrawLoop } from '../../hooks/use-creation-draw-loop'
import { CreationToolsArtboardContext } from './context'
import { ArtboardCropPosition, ArtboardRef } from './types'

const Artboard = styled.div`
    overflow: hidden;
    position: relative;

    canvas {
        display: block;
        height: 100%;
        width: 100%;
    }
`

type Props = {
    className?: string
    canvasScale?: number
    children?: ReactNode
    fps?: number
    crop?: ArtboardCropPosition
    height: number
    width: number
}

const CreationToolsArtboard = (
    { className, height, width, children, canvasScale = 1, fps, crop: defaultCrop }: Props,
    ref: RefObject<ArtboardRef>
) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [renderingContext, setRenderingContext] = useState<CanvasRenderingContext2D | null>(null)
    const [crop, setCrop] = useState<ArtboardCropPosition | undefined>(defaultCrop)
    const frameCount = useCreationDrawLoop(fps)

    // export ref functions
    useImperativeHandle(ref, () => ({
        getFrames: () => {
            if (!canvasRef.current) return []

            // TODO: Update this to work with multiple frames
            const canvas = document.createElement('canvas')
            const context = canvas.getContext('2d')
            const x = crop?.x || 0
            const y = crop?.y || 0

            canvas.width = crop?.width || canvasRef.current.width
            canvas.height = crop?.height || canvasRef.current.height
            context?.drawImage(canvasRef.current, -x, -y)

            return [{ canvas }]
        },
    }))

    // set the rendering context
    useEffect(() => {
        if (canvasRef.current) {
            setRenderingContext(canvasRef.current.getContext('2d'))
        }
    }, [canvasRef, setRenderingContext])

    // clear canvas every re-render
    if (renderingContext) {
        renderingContext.canvas.width = width * canvasScale
        renderingContext.canvas.height = height * canvasScale
    }

    return (
        <CreationToolsArtboardContext.Provider
            value={{
                frameCount,
                renderingContext,
                canvasScale,
                crop,
                setCrop,
            }}
        >
            <Artboard className={className} style={{ width, height }}>
                <canvas ref={canvasRef} />
                {children}
            </Artboard>
        </CreationToolsArtboardContext.Provider>
    )
}

export default forwardRef(CreationToolsArtboard)
